import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces'
import {
  demoGetEduSettings,
  demoGetEduYearSettings,
  demoGetYearHolidays,
  demoListSeriesForRoot,
  demoListSessionDocs,
  demoListSubstituteSessions,
  demoListSeriesForTeacher,
  demoListSeriesForWorkspace,
  demoListSessionDocsForSeries,
} from './eduDemo/eduDemoDirectReads'
import type {
  EduRoom,
  EduSettings,
  EduYearHoliday,
  EduYearSettings,
  EduSessionSeries,
  EduSessionStatus,
  EduSession,
} from '../types/schema'

export type {
  EduRoom,
  EduSettings,
  EduYearHoliday,
  EduYearSettings,
  EduSessionSeries,
  EduSessionStatus,
  EduSession,
}

export interface EduRoomInput {
  id: string
  name: string
  active: boolean
}

export interface SetEduRoomsInput {
  workspaceId: string
  rooms: EduRoomInput[]
}

export interface SetEduRoomsResult {
  success: boolean
  count: number
}

export interface SetEduAlertSettingsInput {
  workspaceId: string
  absenceAlerts: boolean
}

export interface SetEduAlertSettingsResult {
  workspaceId: string
  absenceAlerts: boolean
  success: boolean
}

export interface SetEduYearHolidaysInput {
  workspaceId: string
  rootId: string
  holidays: EduYearHoliday[]
}

export interface SetEduYearHolidaysResult {
  success: boolean
  count: number
}

export interface SaveEduSessionSeriesInput {
  workspaceId: string
  action: 'SAVE' | 'END' | 'DELETE'
  seriesId?: string
  nodeId: string
  teacherUids: string[]
  weekdays: number[]
  startTime: string
  durationMin: number
  fromDate: string
  toDate: string
  roomId?: string
  onlineUrl?: string
  endDate?: string
  bookable?: boolean
  capacity?: number | null
}

export interface SaveEduSessionSeriesResult {
  seriesId: string
  action: 'SAVE' | 'END' | 'DELETE'
}

export type UpdateEduSessionAction =
  | 'CANCEL'
  | 'RESTORE'
  | 'SET_SUBSTITUTE'
  | 'CLEAR_SUBSTITUTE'
  | 'SET_TOPICS'

export interface UpdateEduSessionInput {
  workspaceId: string
  seriesId: string
  date: string
  action: UpdateEduSessionAction
  substituteUid?: string
  topicItemIds?: string[]
  note?: string
}

export interface UpdateEduSessionResult {
  sessionId: string
  success: boolean
}

// ---------------------------------------------------------------------------
// Firestore reads
// ---------------------------------------------------------------------------

export const getEduSettings = async (
  workspaceId: string,
): Promise<EduSettings | null> => {
  if (!workspaceId) return null
  if (isEduDemoWorkspace(workspaceId)) {
    return demoGetEduSettings(workspaceId)
  }
  const snap = await getDoc(doc(firestoreDb, 'edu_settings', workspaceId))
  if (!snap.exists()) return null
  return {
    workspaceId: snap.id,
    ...(snap.data() as Omit<EduSettings, 'workspaceId'>),
  }
}

export const getEduYearSettings = async (
  rootId: string,
): Promise<EduYearSettings | null> => {
  if (!rootId) return null
  if (rootId.startsWith('year-') || rootId.startsWith('prog-') || isEduDemoWorkspace(rootId)) {
    const demo = await demoGetEduYearSettings(rootId)
    if (demo) return demo
  }
  const snap = await getDoc(doc(firestoreDb, 'edu_year_settings', rootId))
  if (!snap.exists()) return null
  return {
    rootId: snap.id,
    ...(snap.data() as Omit<EduYearSettings, 'rootId'>),
  }
}

export const getYearHolidays = async (
  rootId: string,
): Promise<EduYearHoliday[]> => {
  if (rootId.startsWith('year-') || rootId.startsWith('prog-') || isEduDemoWorkspace(rootId)) {
    const demo = await demoGetYearHolidays(rootId)
    if (demo && demo.length > 0) return demo
  }
  const settings = await getEduYearSettings(rootId)
  return settings?.holidays ?? []
}

export const listSeriesForRoot = async (
  workspaceId: string,
  rootId: string,
  restrictedUid?: string,
): Promise<EduSessionSeries[]> => {
  if (!workspaceId || !rootId) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSeriesForRoot(workspaceId, rootId)
  }
  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('rootId', '==', rootId),
  ]
  if (restrictedUid) {
    constraints.push(where('readAccess', 'array-contains', restrictedUid))
  }
  const q = query(collection(firestoreDb, 'edu_session_series'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<EduSessionSeries, 'id'>),
  }))
}

export const listSessionDocs = async (
  workspaceId: string,
  rootId: string,
  fromDate: string,
  toDate: string,
  restrictedUid?: string,
): Promise<EduSession[]> => {
  if (!workspaceId || !rootId) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSessionDocs(workspaceId, rootId, fromDate, toDate)
  }
  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('rootId', '==', rootId),
  ]
  if (restrictedUid) {
    constraints.push(where('readAccess', 'array-contains', restrictedUid))
  }
  constraints.push(
    where('date', '>=', fromDate),
    where('date', '<=', toDate),
    orderBy('date', 'asc'),
  )
  const q = query(collection(firestoreDb, 'edu_sessions'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<EduSession, 'id'>),
  }))
}

export const listSubstituteSessions = async (
  workspaceId: string,
  uid: string,
  fromDate: string,
  toDate: string,
): Promise<EduSession[]> => {
  if (!workspaceId || !uid) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSubstituteSessions(workspaceId, uid, fromDate, toDate)
  }
  const q = query(
    collection(firestoreDb, 'edu_sessions'),
    where('workspaceId', '==', workspaceId),
    where('substituteUid', '==', uid),
    where('date', '>=', fromDate),
    where('date', '<=', toDate),
    orderBy('date', 'asc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<EduSession, 'id'>),
  }))
}

export const listSeriesForTeacher = async (
  workspaceId: string,
  uid: string,
): Promise<EduSessionSeries[]> => {
  if (!workspaceId || !uid) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSeriesForTeacher(workspaceId, uid)
  }
  const q = query(
    collection(firestoreDb, 'edu_session_series'),
    where('workspaceId', '==', workspaceId),
    where('teacherUids', 'array-contains', uid),
  )
  const snap = await getDocs(q)
  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<EduSessionSeries, 'id'>),
  }))
}

export const listSeriesForWorkspace = async (
  workspaceId: string,
): Promise<EduSessionSeries[]> => {
  if (!workspaceId) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSeriesForWorkspace(workspaceId)
  }
  const q = query(
    collection(firestoreDb, 'edu_session_series'),
    where('workspaceId', '==', workspaceId),
    orderBy('fromDate', 'asc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<EduSessionSeries, 'id'>),
  }))
}

export const listSessionDocsForSeries = async (
  workspaceId: string,
  seriesIds: string[],
  fromDate: string,
  toDate: string,
): Promise<EduSession[]> => {
  if (!workspaceId || !seriesIds.length) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSessionDocsForSeries(workspaceId, seriesIds, fromDate, toDate)
  }
  const chunks: string[][] = []
  for (let i = 0; i < seriesIds.length; i += 30) {
    chunks.push(seriesIds.slice(i, i + 30))
  }
  const results: EduSession[] = []
  for (const chunk of chunks) {
    const q = query(
      collection(firestoreDb, 'edu_sessions'),
      where('workspaceId', '==', workspaceId),
      where('seriesId', 'in', chunk),
      where('date', '>=', fromDate),
      where('date', '<=', toDate),
      orderBy('date', 'asc'),
    )
    const snap = await getDocs(q)
    for (const docSnap of snap.docs) {
      results.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<EduSession, 'id'>),
      })
    }
  }
  return results
}

// ---------------------------------------------------------------------------
// Callable wrappers
// ---------------------------------------------------------------------------

export const setEduRooms = async (
  input: SetEduRoomsInput,
): Promise<SetEduRoomsResult> =>
  callable<SetEduRoomsInput, SetEduRoomsResult>('setEduRooms', input)

export const setEduAlertSettings = async (
  input: SetEduAlertSettingsInput,
): Promise<SetEduAlertSettingsResult> =>
  callable<SetEduAlertSettingsInput, SetEduAlertSettingsResult>('setEduAlertSettings', input)

// ---------------------------------------------------------------------------
// Teaching Overview Metrics (OV-D20)
// ---------------------------------------------------------------------------

import { workspaceTodayKey } from './repeat/keys'
import { listDueAssessmentsForWorkspace } from './eduGradingService'

export interface TeachingOverviewMetrics {
  attendanceToday?: number | string | null
  classesToday?: number | null
  assessmentsDue?: number | null
}

export interface LoadTeachingOverviewMetricsOptions {
  timezone?: string | null
  restrictedUid?: string
  now?: Date
}

function addDaysToIsoDate(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + days))
  return dt.toISOString().slice(0, 10)
}

export const loadTeachingOverviewMetrics = async (
  workspaceId: string,
  options?: LoadTeachingOverviewMetricsOptions,
): Promise<TeachingOverviewMetrics> => {
  if (!workspaceId) {
    return { attendanceToday: null, classesToday: null, assessmentsDue: null }
  }
  const todayIso = workspaceTodayKey('DAILY', options?.timezone, options?.now)
  const plus7Iso = addDaysToIsoDate(todayIso, 7)
  const restrictedUid = options?.restrictedUid

  let attendanceToday: string | null
  let classesToday: number | null
  try {
    const series = restrictedUid
      ? await listSeriesForTeacher(workspaceId, restrictedUid)
      : await listSeriesForWorkspace(workspaceId)
    const seriesIds = series.map((s) => s.id).filter(Boolean) as string[]
    const [seriesSessions, subSessions] = await Promise.all([
      seriesIds.length > 0
        ? listSessionDocsForSeries(workspaceId, seriesIds, todayIso, todayIso)
        : Promise.resolve([]),
      restrictedUid
        ? listSubstituteSessions(workspaceId, restrictedUid, todayIso, todayIso)
        : Promise.resolve([]),
    ])

    const sessionMap = new Map<string, EduSession>()
    for (const s of seriesSessions) {
      sessionMap.set(s.id || `${s.seriesId}_${s.date}`, s)
    }
    for (const s of subSessions) {
      const k = s.id || `${s.seriesId}_${s.date}`
      if (!sessionMap.has(k)) sessionMap.set(k, s)
    }
    const sessions = Array.from(sessionMap.values())

    classesToday = sessions.length
    const rollCount = sessions.filter((s) => s.rollTakenAt != null).length
    attendanceToday = classesToday > 0 ? `${rollCount}/${classesToday}` : null
  } catch {
    attendanceToday = null
    classesToday = null
  }

  let assessmentsDue: number | null
  try {
    const assessments = await listDueAssessmentsForWorkspace(
      workspaceId,
      todayIso,
      plus7Iso,
      restrictedUid ? { restrictedUid } : undefined,
    )
    assessmentsDue = assessments.length
  } catch {
    assessmentsDue = null
  }

  return {
    attendanceToday,
    classesToday,
    assessmentsDue,
  }
}

export const setEduYearHolidays = async (
  input: SetEduYearHolidaysInput,
): Promise<SetEduYearHolidaysResult> =>
  callable<SetEduYearHolidaysInput, SetEduYearHolidaysResult>('setEduYearHolidays', input)

export const saveEduSessionSeries = async (
  input: SaveEduSessionSeriesInput,
): Promise<SaveEduSessionSeriesResult> =>
  callable<SaveEduSessionSeriesInput, SaveEduSessionSeriesResult>('saveEduSessionSeries', input)

export const updateEduSession = async (
  input: UpdateEduSessionInput,
): Promise<UpdateEduSessionResult> =>
  callable<UpdateEduSessionInput, UpdateEduSessionResult>('updateEduSession', input)
